import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";

export interface OfferEditor {
  offer_id: string;
  user_id: string;
  added_by: string | null;
  created_at: string;
}

export function useOfferEditors(offerId: string | undefined) {
  return useQuery({
    queryKey: ["offer-editors", offerId],
    enabled: !!offerId,
    queryFn: async (): Promise<OfferEditor[]> => {
      const { data, error } = await supabase
        .from("offer_editors")
        .select("offer_id, user_id, added_by, created_at")
        .eq("offer_id", offerId);
      if (error) throw error;
      return (data ?? []) as OfferEditor[];
    },
  });
}

export function useAddOfferEditor(offerId: string) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.from("offer_editors").insert({
        offer_id: offerId,
        user_id: userId,
        added_by: authUser?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "offer_editors", { offerId });
      toast.success("Medredigerer lagt til");
    },
    onError: (err: Error) => {
      toast.error(`Kunne ikke legge til medredigerer: ${err.message}`);
    },
  });
}

export function useRemoveOfferEditor(offerId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("offer_editors")
        .delete()
        .eq("offer_id", offerId)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await invalidateFor(qc, "offer_editors", { offerId });
      toast.success("Medredigerer fjernet");
    },
    onError: (err: Error) => {
      toast.error(`Kunne ikke fjerne medredigerer: ${err.message}`);
    },
  });
}
